import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/ds";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";
import { NativeSelect } from "@/components/ds/NativeSelect";

/** Ajuste da personalização de um item do pedido. Volta para aprovação do cliente. */
export function EditCustomizationModal({ order, index, item, onClose, onSaved }) {
  const [fields, setFields] = useState(null);
  const [answers, setAnswers] = useState(
    Object.fromEntries(
      (item.customization || []).map((c) => [c.name, { value: c.value, filament_id: c.filament_id }])
    )
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get("/public/catalog")
      .then(({ data }) => {
        const row = data.find((p) => p.id === item.product_id);
        setFields(row?.custom_fields || (item.customization || []).map((c) => ({
          name: c.name,
          type: c.type || "TEXTO",
          required: false,
          max_length: 60,
          options: [],
          colors: [],
        })));
      })
      .catch((e) => toast.error(apiError(e)));
  }, [item.product_id]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/orders/${order.id}/customization`, {
        item_index: index,
        answers: (fields || []).map((f) => ({
          name: f.name,
          value: answers[f.name]?.value || "",
          filament_id: answers[f.name]?.filament_id || null,
        })),
      });
      toast.success("Personalização atualizada. O cliente vai aprovar de novo pelo link.");
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Editar personalização"
      description={`${item.name} — depois de salvar, o pedido volta para aprovação do cliente.`}
      testid="edit-customization-modal"
    >
      <form onSubmit={submit} className="mt-2 flex w-full min-w-0 flex-col gap-4">
        {fields === null && <p className="text-sm text-muted-foreground">Carregando campos...</p>}
        {(fields || []).map((f) => (
          <Field key={f.name} label={f.name} hint={f.required ? "Obrigatório" : ""}>
            {f.type === "COR" && (f.colors || []).length > 0 ? (
              <NativeSelect
                value={answers[f.name]?.value || ""}
                onChange={(v) => {
                  const c = (f.colors || []).find((x) => x.name === v);
                  setAnswers((prev) => ({
                    ...prev,
                    [f.name]: { value: v, filament_id: c?.filament_id || null },
                  }));
                }}
                placeholder="Escolha a cor"
                options={(f.colors || []).map((c) => ({ value: c.name, label: c.name }))}
                data-testid={`edit-custom-color-${f.name}`}
              />
            ) : f.type === "SELECAO" && (f.options || []).length > 0 ? (
              <NativeSelect
                value={answers[f.name]?.value || ""}
                onChange={(v) => setAnswers((prev) => ({ ...prev, [f.name]: { value: v } }))}
                placeholder="Escolha uma opção"
                options={(f.options || []).map((o) => ({ value: o, label: o }))}
                data-testid={`edit-custom-option-${f.name}`}
              />
            ) : (
              <Input
                value={answers[f.name]?.value || ""}
                maxLength={f.max_length || 60}
                onChange={(e) =>
                  setAnswers((prev) => ({ ...prev, [f.name]: { value: e.target.value } }))
                }
                data-testid={`edit-custom-text-${f.name}`}
              />
            )}
          </Field>
        ))}
        <Button
          type="submit"
          disabled={busy || fields === null}
          className="h-11 gap-2 rounded-full font-semibold"
          data-testid="save-customization-btn"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Salvar e enviar para aprovação
        </Button>
      </form>
    </ResponsiveModal>
  );
}
